import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Feature flags context.
 *
 * The backend exposes a list of named features (Video Producer, Interview
 * Practice, etc.) and each one can be in state:
 *   - "enabled"     → render normally
 *   - "coming_soon" → render as muted "Coming soon" badge, not clickable
 *   - "hidden"      → don't render at all
 *
 * Admins use the Admin page to flip those states without touching code.
 *
 * Usage:
 *   const { flags, isHidden, isComingSoon } = useFeatureFlags();
 *   if (isHidden("video_producer")) return null;
 */
const FeatureFlagsContext = createContext({
  flags: {},
  known: [],
  loaded: false,
  refresh: () => {},
  setFlag: async () => {},
  isHidden: () => false,
  isComingSoon: () => false,
  isEnabled: () => true,
  isNew: () => false,
});

export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState({});
  const [known, setKnown] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/feature-flags");
      setFlags(data?.flags || {});
      setKnown(data?.known || []);
    } catch {
      // 401/network → leave flags empty; defaults to "enabled" in helpers
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  const setFlag = async (key, state) => {
    const { data } = await api.put("/admin/feature-flags", { key, state });
    setFlags(data?.flags || data || {});
    return data;
  };

  const isHidden = (key) => flags[key] === "hidden";
  const isComingSoon = (key) => flags[key] === "coming_soon";
  const isNew = (key) => flags[key] === "new";
  // Missing key (e.g. flags not loaded yet) defaults to enabled — never
  // accidentally hide features just because the request was slow.
  const isEnabled = (key) => !flags[key] || flags[key] === "enabled" || flags[key] === "new";

  return (
    <FeatureFlagsContext.Provider value={{ flags, known, loaded, refresh, setFlag, isHidden, isComingSoon, isNew, isEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
